import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { KeyRound, User, Lock, CheckCircle2, AlertCircle, Building2 } from "lucide-react";

const API = "https://api.komorotel.com/api/superadmin";

export default function ActivateAccount() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);        // { purpose, email, hasHotel, hotelName }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [success, setSuccess] = useState(null);  // { message, redirect }

  useEffect(() => {
    axios.get(`${API}/activate/${token}`)
      .then(r => setInfo(r.data))
      .catch(err => setLoadError(err.response?.data?.message || "Lien invalide ou expiré."))
      .finally(() => setLoading(false));
  }, [token]);

  const isActivation = info?.purpose === "activation";

  const handleSubmit = async () => {
    setFormError(null);

    if (isActivation && username.trim().length < 3) {
      setFormError("Choisissez un identifiant d'au moins 3 caractères.");
      return;
    }
    if (password.length < 8) {
      setFormError("Le mot de passe doit faire au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setFormError("Les deux mots de passe ne correspondent pas.");
      return;
    }

    setSubmitting(true);
    try {
      const body = isActivation ? { username: username.trim(), password } : { password };
      const r = await axios.post(`${API}/activate/${token}`, body);
      setSuccess(r.data);
      setTimeout(() => navigate(r.data.redirect || "/admin"), 2500);
    } catch (err) {
      setFormError(err.response?.data?.message || "Une erreur est survenue. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center p-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-6">
          <p className="text-xl font-semibold text-gray-900 tracking-tight">Komorotel</p>
          <p className="text-xs text-gray-400 mt-0.5">Espace hôtelier</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">

          {/* ── ÉTAT : CHARGEMENT ── */}
          {loading && (
            <div className="py-12 text-center text-sm text-gray-400">Vérification du lien…</div>
          )}

          {/* ── ÉTAT : LIEN INVALIDE ── */}
          {!loading && loadError && (
            <div className="py-8 text-center flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                <AlertCircle className="text-red-500" size={22} />
              </div>
              <p className="text-sm font-medium text-gray-900">{loadError}</p>
              <p className="text-xs text-gray-400">
                Contactez l'équipe Komorotel pour recevoir un nouveau lien.
              </p>
            </div>
          )}

          {/* ── ÉTAT : SUCCÈS ── */}
          {!loading && !loadError && success && (
            <div className="py-8 text-center flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[var(--k-green-soft)] flex items-center justify-center">
                <CheckCircle2 className="text-[var(--k-green)]" size={22} />
              </div>
              <p className="text-sm font-medium text-gray-900">{success.message}</p>
              <p className="text-xs text-gray-400">
                Redirection en cours{success.redirect === "/hotel/create" ? " vers la création de votre hôtel" : " vers votre espace"}…
              </p>
            </div>
          )}

          {/* ── ÉTAT : FORMULAIRE ── */}
          {!loading && !loadError && !success && info && (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[var(--k-green-soft)] flex items-center justify-center shrink-0">
                  <KeyRound className="text-[var(--k-green-text)]" size={18} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {isActivation ? "Activez votre compte" : "Nouveau mot de passe"}
                  </p>
                  <p className="text-xs text-gray-400">{info.email}</p>
                </div>
              </div>

              {isActivation && info.hasHotel && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 rounded-lg mb-4">
                  <Building2 size={14} className="text-gray-400 shrink-0" />
                  <p className="text-xs text-gray-600">
                    Établissement : <span className="font-medium text-gray-900">{info.hotelName}</span>
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-3">
                {isActivation && (
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Identifiant de connexion</label>
                    <div className="relative">
                      <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        placeholder="ex. hotel-karthala"
                        autoComplete="username"
                        className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Mot de passe</label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="8 caractères minimum"
                      autoComplete="new-password"
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Confirmer le mot de passe</label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="password"
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleSubmit()}
                      placeholder="Retapez votre mot de passe"
                      autoComplete="new-password"
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
                    />
                  </div>
                </div>

                {formError && (
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg">
                    <AlertCircle size={14} className="text-red-500 shrink-0" />
                    <p className="text-xs text-red-600">{formError}</p>
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full py-2.5 bg-[var(--k-green)] text-white text-sm font-medium rounded-lg hover:bg-[var(--k-green-hover)] transition disabled:opacity-50 mt-1"
                >
                  {submitting
                    ? "Un instant…"
                    : isActivation ? "Activer mon compte" : "Mettre à jour le mot de passe"}
                </button>

                {isActivation && !info.hasHotel && (
                  <p className="text-xs text-gray-400 text-center mt-1">
                    Après l'activation, vous pourrez créer la fiche de votre établissement.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          © {new Date().getFullYear()} Komorotel — Plateforme de réservation aux Comores
        </p>
      </div>
    </div>
  );
}
